import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  Plugin,
  ScriptableContext,
} from "chart.js";
import { Line } from "react-chartjs-2";

import { formatCurrency } from "lib/utils";
import { Info } from "components/common/icons";
import styles from "./results.module.css";

// Custom position for axis labels
const customAxisLabels: Plugin<"line"> = {
  id: "customAxisLabels",
  afterDraw(chart) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;

    // Y axis label position
    ctx.save();
    ctx.fillStyle = "#7B7979";
    ctx.font = "12px Lato, sans-serif";
    ctx.translate(chartArea.left + 16, chartArea.top + 8);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = "right";
    ctx.textBaseline = "bottom";
    ctx.fillText("Cumulative Contributions", 0, 0);
    ctx.restore();

    // X axis label position
    ctx.save();
    ctx.fillStyle = "#7B7979";
    ctx.font = "12px Lato, sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText("Age", chartArea.left - 40, chartArea.bottom + 10);
    ctx.restore();
  },
};

type StripedPatternContext = CanvasRenderingContext2D & {
  _stripedPattern?: CanvasPattern;
};

function getStripesPattern(
  ctx: StripedPatternContext,
): CanvasPattern | undefined {
  if (ctx._stripedPattern) {
    return ctx._stripedPattern;
  }
  const patternCanvas = document.createElement("canvas");
  patternCanvas.width = 8;
  patternCanvas.height = 6;

  const pctx = patternCanvas.getContext("2d");
  if (!pctx) return undefined;

  pctx.fillStyle = "#f5c242";
  pctx.fillRect(0, 0, 4, 6);
  pctx.fillStyle = "#ffffff";
  pctx.fillRect(4, 0, 4, 6);

  const pattern = ctx.createPattern(patternCanvas, "repeat") || undefined;
  ctx._stripedPattern = pattern;
  return pattern;
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  customAxisLabels,
);

const chartConfig = {
  startEarly: {
    label: "Early Start",
    color: "#F5C242",
  },
  startLater: {
    label: "Delayed Start",
    color: "#ED1A3D",
  },
  startLaterLine: {
    label: "Delayed Start (Line)",
    color: "#ED1A3D",
  },
} as const;

function calculateAccumulatedAmount(
  monthlyContribution: number,
  totalMonths: number,
) {
  return monthlyContribution * totalMonths;
}

function calculateTotalContributions(
  goal: number,
  monthlyInterestRate: number,
  monthsNeeded: number,
): number {
  return (
    ((goal * monthlyInterestRate) /
      (Math.pow(1 + monthlyInterestRate, monthsNeeded) - 1)) *
    monthsNeeded
  );
}

function calculateMonthlyContributions(
  goal: number,
  monthlyInterestRate: number,
  monthsNeeded: number,
): number {
  return (
    (goal * monthlyInterestRate) /
    (Math.pow(1 + monthlyInterestRate, monthsNeeded) - 1)
  );
}

interface ResultsProps {
  formData: {
    age: number | null;
    timeline: number | null;
    goal: string | null;
    amount: number | null;
  };
}

const Results: React.FC<ResultsProps> = ({ formData: initialFormData }) => {
  const formData = {
    age: initialFormData.age ?? 0,
    timeline: initialFormData.timeline ?? 0,
    goal: initialFormData.goal ?? "",
    amount: initialFormData.amount ?? 0,
  };

  const startAge = formData.age;
  const endAge = startAge + formData.timeline;
  const timeline = formData.timeline;
  const goal = formData.amount ?? 0;
  const interestRate = 0.08;

  const monthlyInterestRate = interestRate / 12;
  const earlyMonthsNeeded = (endAge - startAge) * 12;
  const delayedMonthsNeeded = (endAge - (startAge + 5)) * 12;

  const earlyTotalContributions = calculateTotalContributions(
    goal,
    monthlyInterestRate,
    earlyMonthsNeeded,
  );
  const earlyMonthlyContributions = calculateMonthlyContributions(
    goal,
    monthlyInterestRate,
    earlyMonthsNeeded,
  );

  const delayedMonthlyContributions = calculateMonthlyContributions(
    goal,
    monthlyInterestRate,
    delayedMonthsNeeded,
  );
  const delayedTotalContributions = calculateTotalContributions(
    goal,
    monthlyInterestRate,
    delayedMonthsNeeded,
  );

  const difference = delayedTotalContributions - earlyTotalContributions;

  //  Build chart data
  const chartDataArray = useMemo(() => {
    const arr = Array.from({ length: timeline + 1 }, (_, i) => {
      const currentAge = startAge + i;

      const investmentStartAgeEarly = startAge + 1;
      const investmentStartAgeLater = startAge + 6;

      let accumulatedEarly: number | null = null;
      let accumulatedLater: number | null = null;

      // Calculate startEarly
      if (currentAge === startAge) {
        accumulatedEarly = 0;
      } else if (
        currentAge >= investmentStartAgeEarly &&
        currentAge <= investmentStartAgeEarly + timeline - 1
      ) {
        const yearsElapsedEarly = currentAge - investmentStartAgeEarly + 1;
        const monthsElapsedEarly = yearsElapsedEarly * 12;
        accumulatedEarly = Math.min(
          calculateAccumulatedAmount(
            earlyMonthlyContributions,
            monthsElapsedEarly,
          ),
          goal,
        );
      }

      // Calculate startLater
      if (currentAge === startAge) {
        accumulatedLater = null;
      } else if (currentAge === startAge + 5) {
        accumulatedLater = 0;
      } else if (
        currentAge >= investmentStartAgeLater &&
        currentAge <= investmentStartAgeLater + timeline - 1
      ) {
        const yearsElapsedLater = currentAge - investmentStartAgeLater + 1;
        const monthsElapsedLater = yearsElapsedLater * 12;
        accumulatedLater = Math.min(
          calculateAccumulatedAmount(
            delayedMonthlyContributions,
            monthsElapsedLater,
          ),
          goal,
        );
      } else {
        accumulatedLater = null;
      }

      return {
        age: currentAge,
        startEarly: accumulatedEarly,
        startLater: accumulatedLater,
        startLaterLine: accumulatedLater,
      };
    }).filter(
      (dataPoint) =>
        typeof dataPoint?.startEarly === "number" ||
        typeof dataPoint?.startLater === "number",
    );

    return arr;
  }, [
    startAge,
    timeline,
    goal,
    earlyMonthlyContributions,
    delayedMonthlyContributions,
  ]);

  // Final points for Early vs. Later.
  const lastIndex = chartDataArray.length - 1;
  const lastEarly = chartDataArray[lastIndex]?.startEarly;
  const lastLater = chartDataArray[lastIndex]?.startLater;

  // Vertical line dataset for Early vs. Later.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let verticalLineDataset: any = null;
  if (lastEarly != null && lastLater != null) {
    const earlyDot = {
      label: "VerticalRefEarly", // This one will still be filtered from tooltip
      data: chartDataArray.map((d) => (d.age === endAge ? lastEarly : null)),
      borderColor: "#4F4C4D",
      pointRadius: 4,
      borderWidth: 3,
      pointBackgroundColor: "#fff",
      pointBorderColor: "#4F4C4D",
      pointHoverRadius: 4,
      pointHoverBackgroundColor: "#fff",
      pointHoverBorderColor: "#4F4C4D",
      hoverBorderWidth: 3,
      order: -1,
    };

    const laterDot = {
      label: "VerticalRefLater", // This one will still be filtered from tooltip
      data: chartDataArray.map((d) => (d.age === endAge ? lastLater : null)),
      borderColor: "#4F4C4D",
      pointRadius: 4,
      borderWidth: 3,
      pointBackgroundColor: "#fff",
      pointBorderColor: "#4F4C4D",
      pointHoverRadius: 4,
      pointHoverBackgroundColor: "#fff",
      pointHoverBorderColor: "#4F4C4D",
      hoverBorderWidth: 3,
      order: -1,
    };

    // Just the connecting line (no dots)
    const connectingLine = {
      label: "VerticalRef", // This one will still be filtered from tooltip
      data: [
        { x: endAge, y: lastEarly },
        { x: endAge, y: lastLater },
      ],
      fill: false,
      borderColor: "#4F4C4D",
      borderDash: [4, 8],
      borderWidth: 3,
      borderDashOffset: 2,
      borderJoinStyle: "round",
      borderCapStyle: "round",
      hoverBorderWidth: 3,
      pointRadius: 0, // No dots on the line itself
      order: 1,
    };

    verticalLineDataset = [connectingLine, earlyDot, laterDot];
  }

  // Chart.js Data + Options
  const data = {
    labels: chartDataArray.map((d) => d.age),
    datasets: [
      // Delayed Start (Line) => no fill
      {
        label: chartConfig.startLaterLine.label,
        data: chartDataArray.map((d) => d.startLaterLine),
        borderColor: chartConfig.startLaterLine.color,
        backgroundColor: "transparent",
        fill: false,
        tension: 0.4,
        borderWidth: 4,
        borderJoinStyle: "round",
        borderCapStyle: "round",
        pointRadius: 0,
        pointHoverBackgroundColor: chartConfig.startLaterLine.color,
      },
      // Equivalent to <Area dataKey="startEarly" />
      {
        label: chartConfig.startEarly.label,
        data: chartDataArray.map((d) => d.startEarly),
        borderColor: chartConfig.startEarly.color,
        backgroundColor: (context: ScriptableContext<"line">) => {
          const canvasCtx = context.chart.ctx as StripedPatternContext;
          return getStripesPattern(canvasCtx);
        },
        fill: true,
        tension: 0.4,
        borderWidth: 4,
        borderJoinStyle: "round",
        borderCapStyle: "round",
        pointRadius: 0,
        pointHoverBackgroundColor: chartConfig.startEarly.color,
      },
      // Equivalent to <Area dataKey="startLater" />
      {
        label: chartConfig.startLater.label,
        data: chartDataArray.map((d) => d.startLater),
        borderColor: chartConfig.startLater.color,
        backgroundColor: "rgba(237, 26, 61, 0.5)",
        fill: true,
        tension: 0.4,
        borderWidth: 4,
        borderJoinStyle: "round",
        borderCapStyle: "round",
        pointRadius: 0,
        pointHoverBackgroundColor: chartConfig.startLater.color,
      },
      ...(verticalLineDataset ? verticalLineDataset : []),
    ],
  };

  const options: import("chart.js").ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    scales: {
      x: {
        title: { display: false },
        ticks: {
          callback: function (value, index) {
            const numericValue = Number(this.getLabelForValue(value as number));
            const atEndAge = numericValue === endAge;
            const atEndAgeMinus1 = numericValue === endAge - 1;
            if (index % 5 !== 0) {
              return atEndAge ? numericValue : "";
            } else {
              return atEndAgeMinus1 ? "" : numericValue;
            }
          },
          color: "#7B7979",
          maxRotation: 0,
        },
        grid: { display: false },
      },
      y: {
        title: { display: false },
        border: { dash: [6, 4] },
        ticks: {
          callback: function (value) {
            const num = Number(value);
            return num ? formatCurrency(num) : "";
          },
          color: "#7B7979",
          maxTicksLimit: 6,
          padding: 8,
        },
        grid: {
          display: true,
          color: "rgba(0,0,0,0.1)",
          drawTicks: false,
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        // Hide “Delayed Start (Line)” & “VerticalRef” from the tooltip
        filter: (tooltipItem) => {
          const label = tooltipItem.dataset.label || "";
          return (
            label !== chartConfig.startLaterLine.label &&
            label !== "VerticalRef" &&
            label !== "VerticalRefEarly" &&
            label !== "VerticalRefLater"
          );
        },

        enabled: true,
        callbacks: {
          title: (tooltipItems) => {
            const age = tooltipItems[0].label;
            return `Total Contributions (Age ${age})`;
          },
          label: (ctx) => {
            const label = ctx.dataset.label || "";
            const rawValue = ctx.parsed.y;
            return `${label}: ${formatCurrency(Number(rawValue))}`;
          },
          labelColor: (ctx) => ({
            borderColor: "#fff",
            size: 4,
            backgroundColor: ctx.dataset.borderColor as string,
            borderRadius: 2,
          }),
        },

        // Styling
        //box
        backgroundColor: "#fff",
        cornerRadius: 4,
        borderWidth: 1,
        caretSize: 0,
        borderColor: "rgb(233 233 233)",

        // title
        titleColor: "rgb(35 31 32 / 1)",
        titleMarginBottom: 2,
        titleFont: {
          family: "Lato, Arial, sans-serif",
          size: 12,
          weight: "normal",
        },

        // body
        bodyColor: "rgb(79 76 77 / 1)",
        bodyFont: {
          family: "Lato, Arial, sans-serif",
          size: 12,
        },
      },
    },
  };

  return (
    <div>
      <div className={styles.relative}>
        <div className={styles.chartContainer}>
          <div className={styles.chartInner}>
            <Line data={data} options={options} />
          </div>
        </div>

        <div className={styles.bannerContainer}>
          <Info className={styles.infoIcon} />
          <Banner difference={difference} goal={goal} />
          <div className={styles.arrow} />
        </div>
      </div>

      <div className={styles.bottomContainer}>
        <div className={styles.legendContainer}>
          <div className={styles.legend}>
            <div className={styles.legendItem}>
              <div className={styles.legendColorEarly} />
              Start at age {formData.age}
            </div>
            <div className={styles.legendItem}>
              <div className={styles.legendColorLater} />
              Start at age {Number(formData.age) + 5}
            </div>
          </div>
        </div>

        <div className={styles.mobileBanner}>
          <Info className={styles.infoIcon} />
          <Banner difference={difference} goal={goal} />
        </div>

        <div className={styles.description}>
          <p>
            Starting early and harnessing the power of compound interest allows
            you to achieve your goals with less effort and lower contributions.{" "}
            <b>The sooner you begin, the more time your money has to grow,</b>{" "}
            making it easier to secure your financial future.
          </p>
        </div>
      </div>
    </div>
  );
};

const Banner = ({ difference, goal }: { difference: number; goal: number }) => {
  return (
    <p>
      Starting 5 years later would cost you an additional{" "}
      <b>{formatCurrency(difference)}</b> to reach the same goal of{" "}
      {formatCurrency(goal)}.
    </p>
  );
};

export default Results;
